import { useState } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { LayoutDashboard, Users, Building2, ShieldAlert, Key, ChevronLeft, ChevronRight, LogOut, Menu, Search, BarChart3, LineChart } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { useNoindex } from '@/hooks/useNoindex';

export function AdminLayout() {
  // The admin console is never indexed.
  useNoindex();

    const location = useLocation();
    const [isCollapsed, setIsCollapsed] = useState(false);

    const navigation = [
        { name: 'Dashboard', href: '/admin', icon: LayoutDashboard },
        { name: 'Website', href: '/admin/website', icon: BarChart3 },
        { name: 'Activation', href: '/admin/activation', icon: LineChart },
        { name: 'User Management', href: '/admin/users', icon: Users },
        { name: 'Org Management', href: '/admin/organizations', icon: Building2 },
        { name: 'SAML Overview', href: '/admin/saml', icon: Key },
        { name: 'Prospecting', href: '/admin/prospecting', icon: Search },
    ];

    const SidebarContent = ({ className, collapsed = false, onNavigate }: { className?: string, collapsed?: boolean, onNavigate?: () => void }) => (
        <div className={cn("bg-slate-900 text-white flex flex-col h-full", className)}>
            <div className={cn("p-6 flex items-center mb-2", collapsed ? "justify-center px-4" : "")}>
                <div className={cn("flex items-center gap-2 text-red-600 font-bold text-xl uppercase tracking-widest transition-all", collapsed ? "justify-center" : "")}>
                    <ShieldAlert className="w-8 h-8 shrink-0" />
                    {!collapsed && (
                        <div className="overflow-hidden whitespace-nowrap">
                            <span>God Mode</span>
                        </div>
                    )}
                </div>
            </div>
            {!collapsed && <div className="px-6 pb-2 -mt-4"><p className="text-xs text-slate-500 truncate">System Administration</p></div>}

            <nav className="flex-1 px-4 space-y-2 mt-2">
                <TooltipProvider delayDuration={0}>
                    {navigation.map((item) => {
                        const isActive = location.pathname === item.href || (item.href !== '/admin' && location.pathname.startsWith(item.href));

                        const LinkContent = (
                            <Link
                                to={item.href}
                                onClick={onNavigate}
                                className={cn(
                                    'flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-colors',
                                    isActive
                                        ? 'bg-red-600/10 text-red-500'
                                        : 'text-slate-400 hover:bg-slate-800 hover:text-white',
                                    collapsed && "justify-center px-2"
                                )}
                            >
                                <item.icon className="w-5 h-5 shrink-0" />
                                {!collapsed && <span>{item.name}</span>}
                            </Link>
                        );

                        if (collapsed) {
                            return (
                                <Tooltip key={item.name}>
                                    <TooltipTrigger asChild>
                                        {LinkContent}
                                    </TooltipTrigger>
                                    <TooltipContent side="right" sideOffset={10}>
                                        <p>{item.name}</p>
                                    </TooltipContent>
                                </Tooltip>
                            )
                        }

                        return <div key={item.name}>{LinkContent}</div>
                    })}
                </TooltipProvider>
            </nav>

            <div className="p-4 border-t border-slate-800 mt-auto space-y-2">
                {!collapsed && (
                    <Button asChild variant="ghost" className="w-full justify-start text-slate-400 hover:text-white hover:bg-slate-800 text-red-400 hover:text-red-300 hover:bg-red-900/20">
                        <Link to="/dashboard" className="flex items-center pl-1">
                            <LogOut className="w-5 h-5 mr-3" />
                            Exit God Mode
                        </Link>
                    </Button>
                )}
                {collapsed && (
                    <TooltipProvider delayDuration={0}>
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <Button asChild variant="ghost" size="sm" className="w-full justify-center px-0 text-slate-400 hover:text-white hover:bg-slate-800">
                                    <Link to="/dashboard">
                                        <LogOut className="w-5 h-5" />
                                    </Link>
                                </Button>
                            </TooltipTrigger>
                            <TooltipContent side="right" className="bg-slate-800 text-white border-slate-700 ml-2">
                                Exit God Mode
                            </TooltipContent>
                        </Tooltip>
                    </TooltipProvider>
                )}
            </div>
        </div>
    );

    return (
        <div className="h-screen bg-gray-100 dark:bg-gray-900 flex flex-col md:flex-row overflow-hidden">
            {/* Mobile Header */}
            <div className="md:hidden bg-slate-900 text-white p-4 flex items-center justify-between border-b border-slate-800 sticky top-0 z-50">
                <div className="flex items-center gap-2 text-red-600 font-bold uppercase tracking-widest">
                    <ShieldAlert className="w-6 h-6" />
                    <span>God Mode</span>
                </div>
                <Sheet>
                    <SheetTrigger asChild>
                        <Button variant="ghost" size="icon" className="text-slate-400 hover:text-white">
                            <Menu className="w-6 h-6" />
                        </Button>
                    </SheetTrigger>
                    <SheetContent side="left" className="p-0 border-r-slate-800 bg-slate-900 w-72 text-white border-none">
                        <SidebarContent />
                    </SheetContent>
                </Sheet>
            </div>

            {/* Desktop Sidebar */}
            <div
                className={cn(
                    "hidden md:flex flex-col border-r border-slate-800 overflow-y-auto shrink-0 transition-all duration-300 ease-in-out h-full bg-slate-900",
                    isCollapsed ? "w-20" : "w-64"
                )}
            >
                <SidebarContent collapsed={isCollapsed} className="bg-transparent" />

                {/* Collapse Button (Only on Desktop) */}
                <div className="px-4 pb-4">
                    <Button
                        variant="ghost"
                        size="sm"
                        className={cn("w-full text-slate-400 hover:text-white hover:bg-slate-800", isCollapsed ? "justify-center px-0" : "justify-start pl-4")}
                        onClick={() => setIsCollapsed(!isCollapsed)}
                    >
                        {isCollapsed ? <ChevronRight className="w-5 h-5" /> : <ChevronLeft className="w-5 h-5" />}
                        {!isCollapsed && <span className="ml-2">Collapse</span>}
                    </Button>
                </div>
            </div>

            {/* Main Content */}
            <div className="flex-1 overflow-auto relative">
                <div className="p-4 md:p-8 max-w-7xl mx-auto">
                    <Outlet />
                </div>
            </div>
        </div>
    );
}
